import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, OnDestroy, ViewChild, NgZone, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from 'src/app/web-generator/generator-env-token';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, debounceTime, map, skip, filter, tap, take } from 'rxjs/operators';
import { GlobalComponentService } from '@techmore/global';
import { DataSearchFormComponent } from '../data-search-form/data-search-form.component';
import { GALoggerService } from 'src/app/share/service/galogger.service';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { checkIsMobile, checkIsTouchMobile, removeUrlQueryParam } from 'src/app/util';
import { TranslateService } from '@ngx-translate/core';
import { TemplateService } from 'src/app/web-generator/template-generator/service/template.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { isNullOrUndefined } from 'util';
import { DataSearchService } from '../../data-search.service';


/** 資料搜尋頁 */
@Component({
  selector: 'app-data-search',
  templateUrl: './data-search.component.html',
  styleUrls: [
    './cms-search-03.component.scss',
    './cms-search-03-tab.scss',
    './cms-search-03-result-tab1.scss',
    './cms-search-03-result-tab2.scss',
    './data-search.component.scss'
  ]
})
export class DataSearchComponent implements OnInit, OnDestroy {
  @ViewChild(InfiniteScrollDirective, { static: false }) infiniteScrollDirective: InfiniteScrollDirective;
  @ViewChild('searchForm', { static: false }) searchForm: DataSearchFormComponent;
  readonly apiHost = this.generatorEnv.apiHost;
  /** 是瀏覽器端渲染 */
  isBrowser: boolean;
  /** 到首頁的路徑 */
  toHomePath: string;
  /** client 分頁一頁資料數量 */
  // protected climit$ = new BehaviorSubject<number>(isMobile ? 6 : 16);
  protected climit$ = new BehaviorSubject<number>(checkIsMobile() ? 5 : 12);
  set climit(value: number) { this.climit$.next(value); }
  get climit(): number { return this.climit$.getValue(); }
  /** client 分頁資料位移量 */
  protected coffset$ = new BehaviorSubject<number>(0);
  set coffset(value: number) { this.coffset$.next(value); }
  get coffset() { return this.coffset$.getValue(); }
  /** client 分頁所有資料頁數 */
  ctotalPages: number;
  /** client 分頁所有資料筆數 */
  ctotal: number;
  /** client 分頁的查詢結果 */
  cPagingSearchResult: any[];
  private clientPagingChangeSubc: Subscription;
  /** 螢幕大小縮放自動去更新位置 */
  private updateStickySubc: Subscription;
  /** 螢幕大小縮放自動去更新limit */
  private updateLimitSubc: Subscription;
  /** 列表顯示模式是否在動畫過渡中 */
  isViewModeAnimating = false;
  /** 列表顯示模式，1是圖片模式，2是圖文模式 */
  protected viewMode$ = new BehaviorSubject<number>(1);
  /** 列表顯示模式，1是圖片模式，2是圖文模式 */
  set viewMode(value: number) { this.viewMode$.next(value); }
  /** 列表顯示模式，1是圖片模式，2是圖文模式 */
  get viewMode() { return this.viewMode$.getValue(); }
  /** 目前設備是否為可觸碰螢幕裝置 */
  isTouchScreen: boolean;
  protected routerEvtSubc: Subscription;
  protected keepUrlSubscription: Subscription;
  private webTitleSubscription: Subscription;
  /** 是否為自動切換圖文模式 */
  isAutoViewMode = true;
  /** 是否顯示黏貼畫面頂部的搜尋按鈕 */
  showStickySearchBtn = false;
  /** 滾動事件廣播器 */
  private scrollEvtNotifyer = new Subject<Event>();
  private scrollToggleStickySearchBtnSubc: Subscription;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
    @Inject(PLATFORM_ID) private platformId: object,
    private serverConfigService: ServerConfigService,
    private zone: NgZone,
    private elRef: ElementRef,
    private gALoggerService: GALoggerService,
    private dataSearchService: DataSearchService,
    private router: Router,
    private route: ActivatedRoute,
    private templateService: TemplateService,
    private translateService: TranslateService,
    private render: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.toHomePath = `/${serverConfigService.config.lang}`;
  }

  ngOnInit() {
    this.render.setAttribute(this.document.head.querySelector('link[rel="canonical"]'), 'href', removeUrlQueryParam(this.serverConfigService.getCurrentUrl()));
    this.render.setAttribute(this.document.head.querySelector('meta[property="og:url"]'), 'content', removeUrlQueryParam(this.serverConfigService.getCurrentUrl()));
    this.render.setAttribute(this.document.head.querySelector('meta[name="twitter:url"]'), 'content', removeUrlQueryParam(this.serverConfigService.getCurrentUrl()));

    this.webTitleSubscription = combineLatest(
      this.templateService.siteInfo$,
      this.translateService.get('searchpage.title'),
    ).subscribe(([siteInfo, s]) => {
      this.templateService.setWebTitle({ title: `${s}｜${siteInfo.metadata.title}`, desc: '', image: '' });
    });

    if (this.isBrowser) {
      this.initParamsFromUrl();

      const queryObservable = this.viewMode$.pipe(
        debounceTime(50),
        distinctUntilChanged(),
      );
      this.keepParamsToUrl(queryObservable);
      this.autoViewMode();
      this.listenOnClearParamRouteeEvt();
      this.startUpdateLimit();
      this.isTouchScreen = checkIsTouchMobile();
      this.listenOnScrollToggleStickySearch();
    }
  }

  /** 載入時網址上的查詢參數同步進來 */
  protected initParamsFromUrl() {
    const queryParams = this.route.snapshot.queryParams;
    if ('viewMode' in queryParams) { this.viewMode$.next(+queryParams['viewMode']); }
  }

  /** 如果篩選是聲音或文件資料類型，又是圖片模式，就預 */
  protected autoViewMode() {
    this.route.queryParamMap.pipe(
      debounceTime(50),
      map(paramMap => {
        const query = paramMap.get('query');
        const queryObj = isNullOrUndefined(query) ? {} : JSON.parse(query);
        const format = queryObj['format'] || '';
        return format;
      }),
      distinctUntilChanged(),
      filter(format => this.viewMode === 1 && this.isAutoViewMode && (format === '聲音' || format === '文件')),
    ).subscribe(format => {
      this.viewMode = 2;
    });
  }

  /** 紀錄參數到網址列上 */
  protected keepParamsToUrl(params$: Observable<any>) {
    this.keepUrlSubscription = this.viewMode$.pipe(
      debounceTime(50),
      distinctUntilChanged(),
      skip(1),
      map(viewMode => ({viewMode})),
    ).subscribe((params) => this.router.navigate(['.'], { queryParams: params, relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge' }));
  }

  /** 監聽畫面參數被清空的時候 */
  protected listenOnClearParamRouteeEvt() {
    let currentQP = this.route.snapshot.queryParams;
    this.routerEvtSubc = this.router.events
      .pipe(
        filter(evt => evt instanceof NavigationEnd),
        map((evt: NavigationEnd) => {
          return this.route.snapshot.queryParams;
        }),
        filter(qp => {
          let ret: boolean;
          if (Object.keys(currentQP).length > 0 && Object.keys(qp).length === 0) {
            ret = true;
          } else {
            ret = false;
          }
          currentQP = qp;
          return ret;
        })
      ).subscribe(qp => {
        this.viewMode = 1;
      });
  }

  protected startUpdateLimit() {
    this.updateLimitSubc = fromEvent(window, 'resize')
      .pipe(debounceTime(15))
      .subscribe(evt => {
        this.climit = checkIsMobile() ? 5 : 12;
      });
  }

  protected stopUpdateLimit() {
    if (this.updateLimitSubc) {
      this.updateLimitSubc.unsubscribe();
      delete this.updateLimitSubc;
    }
  }

  ngOnDestroy(): void {
    this.stopWatchingClientPaging();
    this.stopUpdateSticky();
    this.stopUpdateLimit();
    if (this.routerEvtSubc) { this.routerEvtSubc.unsubscribe(); }
    if (this.keepUrlSubscription) { this.keepUrlSubscription.unsubscribe(); }
    if (this.webTitleSubscription) {
      this.webTitleSubscription.unsubscribe();
      delete this.webTitleSubscription;
    }
    this.stopListenOnScrollToggleStickySearch();
  }

  /** 當滾動搜尋區塊時 */
  onScrollResult() {
    if (this.cPagingSearchResult.length === 0) {
      // 還在第一個分頁，不做事
      return;
    }
    if (this.ctotal >= this.coffset + this.climit) {
      this.cpageChange(this.coffset / this.climit + 2);
    }
  }

  /**
   * 當client分頁改變
   * @param page 下一頁的頁數，從 1 開始編號
   */
  cpageChange(page: number) {
    this.coffset = (page - 1) * this.climit;
  }

  startWatchingClientPaging() {
    this.clientPagingChangeSubc = this.coffset$.pipe(
      distinctUntilChanged(),
    ).subscribe(coffset => {
      this.cPagingSearchResult.push(...this.searchForm.searchResult.slice(coffset, coffset + this.climit));
    });
  }

  stopWatchingClientPaging() {
    if (this.clientPagingChangeSubc) {
      this.clientPagingChangeSubc.unsubscribe();
      delete this.clientPagingChangeSubc;
      if (this.infiniteScrollDirective) {
        this.infiniteScrollDirective.destroyScroller();
        this.infiniteScrollDirective.setup();
      }
    }
  }

  /** 當表單送出前 */
  onBeforeLoadData() {
    this.stopWatchingClientPaging();
  }

  /** 當資料載入後 */
  onDataLoaded() {
    // 更新 client 分頁位置
    if (this.searchForm.total > this.searchForm.offset + this.searchForm.limit) {
      this.ctotal = this.searchForm.limit;
    } else {
      this.ctotal = this.searchForm.total - this.searchForm.offset;
    }
    this.ctotalPages = Math.ceil(this.ctotal / this.climit);
    this.coffset = 0;
    this.cPagingSearchResult = [];
    this.startWatchingClientPaging();
  }

  /** 當表單被重設後 */
  onFormReset() {
    this.climit = checkIsMobile() ? 5 : 12;
    this.coffset = 0;
  }

  /** 監看滾動事件就切換顯示黏貼的搜尋按鈕 */
  listenOnScrollToggleStickySearch() {
    window.addEventListener('scroll', this.onScrollHandler
    , { passive: true }
    );
    this.scrollEvtNotifyer.pipe(
      debounceTime(10),
    ).subscribe(event => {
      const searchFormEl = document.querySelector('.top_function') as HTMLElement;
      const elRect = searchFormEl.getBoundingClientRect();
      this.zone.run(() => {
        if (elRect.bottom <= 0) {
          if (!this.showStickySearchBtn) {
            this.showStickySearchBtn = true;
          }
        } else if (elRect.top > 0 && searchFormEl.classList.contains('stick_section')) {
          if (this.showStickySearchBtn) {
            this.showStickySearchBtn = false;
          }
        } else if (elRect.top === 0 && searchFormEl.classList.contains('stick_section')) {
          if (!this.showStickySearchBtn) {
            this.showStickySearchBtn = true;
          }
        } else {
          if (!searchFormEl.classList.contains('stick_section') && this.showStickySearchBtn) {
            this.showStickySearchBtn = false;
          }
        }
      });
    });
  }

  private onScrollHandler = (event: Event) => {
    this.scrollEvtNotifyer.next(event);
  }

  /** 停止監看滾動事件就切換顯示黏貼的搜尋按鈕 */
  stopListenOnScrollToggleStickySearch() {
    window.removeEventListener('scroll', this.onScrollHandler);
    if (this.scrollToggleStickySearchBtnSubc) { this.scrollToggleStickySearchBtnSubc.unsubscribe(), delete this.scrollToggleStickySearchBtnSubc; }
    this.scrollEvtNotifyer.complete();
  }

  /** 將查詢表單黏在畫面上方或取消黏貼 */
  stickSearchForm(selector: string, togglerEl: HTMLElement) {
    const el = this.elRef.nativeElement as HTMLElement;
    const stickyTarget = el.querySelector(selector) as HTMLElement;
    if (stickyTarget.classList.contains('stick_section')) {
      stickyTarget.style.top = `-${stickyTarget.clientHeight}px`;
      togglerEl.style.top = '';
      togglerEl.style.transition = `all .3s linear`;
      timer(300).subscribe(t => {
        stickyTarget.classList.remove('stick_section');
        stickyTarget.style.top = ``;
        togglerEl.style.transition = '';
        this.zone.run(() => {
          // this.isSearchFormSticky = false;
          this.stopUpdateSticky();
        });
      });
    } else {
      stickyTarget.classList.add('stick_section');
      // this.isSearchFormSticky = true;
      stickyTarget.style.top = `-${stickyTarget.clientHeight}px`;
      timer(50).subscribe(t => {
        stickyTarget.style.top = `0px`;
        togglerEl.style.top = `${stickyTarget.clientHeight}px`;
        togglerEl.style.transition = `all .3s linear`;
        this.zone.run(() => {
          this.startUpdateSticky(stickyTarget, togglerEl);
        });
        timer(300).subscribe(t1 => {
          togglerEl.style.transition = '';
        });
      });
    }
  }

  startUpdateSticky(stickyTarget: HTMLElement, togglerEl: HTMLElement) {
    this.updateStickySubc = fromEvent(window, 'resize')
      .pipe(debounceTime(15))
      .subscribe(evt => {
        if (stickyTarget.classList.contains('stick_section')) {
          togglerEl.style.top = `${stickyTarget.clientHeight}px`;
        }
      });
  }

  stopUpdateSticky() {
    if (this.updateStickySubc) {
      this.updateStickySubc.unsubscribe();
      delete this.updateStickySubc;
    }
  }

  /**
   * 導航到資料頁面
   * @param e 點擊事件
   * @param item 列表資料項目
   * @param indexOnList 目前這筆資料在當前分頁中的位置序
   */
  toDetailPage(e: UIEvent, item: any, indexOnList: number) {
    e.preventDefault();
    const anch = e.currentTarget as HTMLAnchorElement;
    let linkHref = anch.getAttribute('href');
    const linkUrl = anch.href;

    if (this.searchForm.needLog && this.searchForm.keyword) {
      this.gALoggerService.log({
        query_keyword: this.searchForm.keyword,
        search_type: this.searchForm.isFuzzyMode ? 1 : 0,
        total_results: this.searchForm.total,
        click_ranks: this.searchForm.offset + indexOnList + 1,
        page_id: item['materialId'],
        fetch_index: item['fetchIndex'],
        click_webpage: linkUrl,
        event: 'click_page',
      });

      // this.dataSearchService.logUser2DetailWithKeyword(
      //   item['fetchIndex'],
      //   item['materialId'],
      //   this.searchForm.keyword,
      //   this.searchForm.searchWithKeywordTimeStamp
      // ).subscribe({
      //   error: error => {
      //     console.error('log search action fail: ', error);
      //   }
      // });
    }

    this.router.navigateByUrl(`${linkHref}`);
  }

  /** 切換顯示模式 */
  toggleViewMode(e: UIEvent) {
    e.preventDefault();
    this.isAutoViewMode = false;
    if (this.viewMode === 1) {
      this.viewMode = 2;
    } else {
      this.viewMode = 1;
    }
    this.isViewModeAnimating = true;
    timer(150).subscribe(t => {
      this.zone.run(() => {
        this.isViewModeAnimating = false;
      });
    });
  }

  /** 當素材圖片載入完成後調用 */
  onMaterialImageSrcLoad(item: any, image: HTMLImageElement) {
    this.zone.run(() => {
      if (image) {
        item['imageNaturalWidth'] = image.naturalWidth;
        item['imageNaturalHeight'] = image.naturalHeight;
      }
    });
  }
}
